import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { EditFieldGrid } from "./EditFieldGrid";
import { buildGroupHierarchy, annotateVisibilityAndFilter, sortFieldsByAppearanceOrder, shouldShowField } from "../utils/grouping";
import { useEdit } from "../context/EditContext";
import { useEditUI } from "../context/EditUIPreferencesContext";
import { getIcon } from "../utils/icons";
import InternalElementsList from "./InternalElementsList";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";
import useMatchMedia from "../../core/hooks/useMatchMedia";
import ElementStylePresetsTab from "./panels/ElementStylePresetsTab";
import { SUPPORTED_ELEMENT_STYLE_PRESET_TYPES } from "./panels/elementStylePresetsApi";

// Render tabs for an arbitrary depth group hierarchy.
// We present at most two visible tab bars at a time: current level and one nested level beneath it.
// The "Basic" (MAIN) tab is synthesized from editMetadata.main_fields.

// Element types that should surface the "Layout" group as the very first tab,
// ahead of the (globally higher-priority) "Style" group. The backend group order
// is global (Style=4, Layout=5), so we reorder here for these specific types only.
const LAYOUT_FIRST_ELEMENT_TYPES = new Set(["hero", "component"]);

const arePathsEqual = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const EMPTY_PATH = [];

const TabBar = ({ tabs, activeIndex, onSelect }) => {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Check if scrolling is needed and update arrow visibility
  const updateArrowVisibility = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const hasOverflow = scrollWidth > clientWidth;
    
    setShowLeftArrow(hasOverflow && scrollLeft > 5);
    setShowRightArrow(hasOverflow && scrollLeft < scrollWidth - clientWidth - 5);
  }, []);

  // Update on mount, tab changes, and window resize
  useEffect(() => {
    updateArrowVisibility();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateArrowVisibility);
    }
    window.addEventListener('resize', updateArrowVisibility);
    
    return () => {
      if (container) {
        container.removeEventListener('scroll', updateArrowVisibility);
      }
      window.removeEventListener('resize', updateArrowVisibility);
    };
  }, [updateArrowVisibility, tabs]);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const scrollAmount = direction === 'left' ? -200 : 200;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      {/* Left arrow with fade */}
      {showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-0 bottom-0 z-10 w-16 flex items-center bg-gradient-to-r from-white via-white to-transparent dark:from-dark-900 dark:via-dark-900 dark:to-transparent hover:brightness-95 transition-all"
          aria-label="Scroll left"
        >
          <svg className={`w-4 h-4 ml-2 ${textColors.secondary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      
      {/* Tabs container */}
      <div 
        ref={scrollContainerRef}
        className={`flex overflow-x-auto border-b ${borders.default} bg-transparent pt-0 scrollbar-hide`}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {tabs.map((t, idx) => (
          <button
            key={t.key}
            onClick={() => onSelect(idx)}
            className={`px-3 py-1 whitespace-nowrap -mb-px border-b-2 ${typography.bodySmall} transition-colors ${
              activeIndex === idx
                ? "border-blue-600 dark:border-blue-400 text-blue-700 dark:text-blue-400 font-medium"
                : `border-transparent ${textColors.tertiary} hover:${textColors.primary} hover:border-gray-300 dark:hover:border-gray-600`
            }`}
          >
            <span className="inline-flex items-center gap-2">
              {t.icon && (
                <span className={textColors.tertiary}>{getIcon(t.icon)}</span>
              )}
              <span>{t.label}</span>
            </span>
          </button>
        ))}
      </div>

      {/* Right arrow with fade */}
      {showRightArrow && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-0 bottom-0 z-10 w-16 flex items-center justify-end bg-gradient-to-l from-white via-white to-transparent dark:from-dark-900 dark:via-dark-900 dark:to-transparent hover:brightness-95 transition-all"
          aria-label="Scroll right"
        >
          <svg className={`w-4 h-4 mr-2 ${textColors.secondary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
};

const FieldsList = ({ fields, values, onChange }) => {
  const hasVisible = useMemo(
    () => sortFieldsByAppearanceOrder(fields).some(({ metadata }) => shouldShowField(metadata, values)),
    [fields, values],
  );
  if (!hasVisible) {
    return <div className={`p-4 ${typography.bodySmall} ${textColors.muted}`}>No fields in this tab.</div>;
  }
  return (
    <EditFieldGrid
      className="mt-3 pb-2"
      fields={fields}
      values={values}
      onChange={onChange}
    />
  );
};

const EditTabs = ({ editMetadata, values, onChange, onEditInternalItem, onDeleteInternalItem }) => {
  const { selectedElement } = useEdit();
  const { selectedTabByType, setSelectedTabPathForType } = useEditUI();
  const isMobileViewport = useMatchMedia("(max-width: 767px)", false);

  const elementType = selectedElement?.type || "_unknown_";
  const persistedPath = selectedTabByType[elementType]?.path || EMPTY_PATH;
  const supportsStylePresets = SUPPORTED_ELEMENT_STYLE_PRESET_TYPES.has(elementType);

  // Calculate internal elements count
  const internalItemsCount = (values?.internal_elements || []).length;
  const hasInternalItems = internalItemsCount > 0;

  // Build hierarchy from groups (excluding MAIN which we treat separately)
  const groupedMetadata = useMemo(() => {
    const copy = {};
    Object.entries(editMetadata || {}).forEach(([k, v]) => {
      if (k === "main_fields") return;
      copy[k] = v;
    });
    return copy;
  }, [editMetadata]);

  const hierarchy = useMemo(() => buildGroupHierarchy(groupedMetadata), [groupedMetadata]);
  const filteredHierarchy = useMemo(() => annotateVisibilityAndFilter(hierarchy, values), [hierarchy, values]);

  // Prepare Basic tab count
  const basicFields = useMemo(() => {
    const arr = Object.entries(editMetadata?.main_fields || {}).map(([field, metadata]) => ({ field, metadata }));
    return arr;
  }, [editMetadata]);
  const basicVisibleCount = useMemo(() => basicFields.filter(({ metadata }) => shouldShowField(metadata, values)).length, [basicFields, values]);

  // Top-level tabs include "Basic", each root group, and optionally "Internal Elements"
  const topTabs = useMemo(() => {
    const tabs = [];
    if (basicVisibleCount > 0) {
      tabs.push({ key: "__basic__", label: "Basic", type: "basic", count: basicVisibleCount, icon: "star" });
    }
    filteredHierarchy.forEach((n) => {
      tabs.push({ key: n.name, label: n.groupInfo?.title || n.name, type: "group", node: n, count: n.visibleCount, icon: n.groupInfo?.icon });
    });
    // Add Internal Elements tab if items exist
    if (hasInternalItems) {
      tabs.push({ 
        key: "__internal__", 
        label: `Internal Elements (${internalItemsCount})`, 
        type: "internal", 
        count: internalItemsCount, 
        icon: "hierarchy" 
      });
    }
    if (supportsStylePresets) {
      tabs.push({
        key: "__styles__",
        label: "Presets",
        type: "styles",
        icon: "paint-palette",
      });
    }
    // For Hero/Component, promote the "Layout" group tab to the front so it is the
    // first tab the user lands on (instead of "Style").
    if (LAYOUT_FIRST_ELEMENT_TYPES.has(elementType)) {
      const layoutIdx = tabs.findIndex((t) => t.key === "layout");
      if (layoutIdx > 0) {
        const [layoutTab] = tabs.splice(layoutIdx, 1);
        tabs.unshift(layoutTab);
      }
    }
    return tabs;
  }, [filteredHierarchy, basicVisibleCount, hasInternalItems, internalItemsCount, supportsStylePresets, elementType]);

  // Single source of truth: EditUIContext persisted path.
  // Avoid local path state + two-way sync effects (they can ping-pong on elementType changes).
  const basePath = persistedPath.length > 0 ? persistedPath : [0];
  const maxTop = Math.max(topTabs.length - 1, 0);
  const activeTopIndex = Math.min(basePath[0] ?? 0, maxTop);

  const activeTop = topTabs[activeTopIndex] || topTabs[0];
  const childNodes = activeTop?.type === "group" ? activeTop.node?.children || [] : [];
  const maxChild = Math.max(childNodes.length - 1, 0);
  const activeChildIndex =
    activeTop?.type === "group" && childNodes.length > 0
      ? Math.min(basePath[1] ?? 0, maxChild)
      : null;

  const clampedPath = useMemo(() => {
    if (!activeTop) return [0];
    if (activeTop.type === "group" && childNodes.length > 0 && activeChildIndex != null) {
      return [activeTopIndex, activeChildIndex];
    }
    return [activeTopIndex];
  }, [activeTop, activeTopIndex, activeChildIndex, childNodes.length]);

  const secondLevelOptions = useMemo(() => {
    if (!childNodes || childNodes.length === 0) return [];
    return childNodes.map((c) => ({
      key: c.name,
      label: c.groupInfo?.title || c.name,
      count: c.visibleCount,
      icon: c.groupInfo?.icon,
    }));
  }, [childNodes]);

  useEffect(() => {
    if (!arePathsEqual(persistedPath, clampedPath)) {
      setSelectedTabPathForType(elementType, clampedPath);
    }
  }, [elementType, persistedPath, clampedPath, setSelectedTabPathForType]);

  const selectTop = useCallback(
    (idx) => {
      const nextTop = Math.min(idx, maxTop);
      setSelectedTabPathForType(elementType, [nextTop]);
    },
    [elementType, maxTop, setSelectedTabPathForType],
  );

  // Compute content for current selection
  let content = null;
  let secondLevelTabs = null;

  if (!activeTop || activeTop.type === "basic") {
    content = <FieldsList fields={basicFields} values={values} onChange={onChange} />;
  } else if (activeTop.type === "internal") {
    // Render Internal Elements list
    content = (
      <InternalElementsList
        elements={values?.internal_elements || []}
        onEdit={onEditInternalItem}
        onDelete={onDeleteInternalItem}
      />
    );
  } else if (activeTop.type === "styles") {
    content = <ElementStylePresetsTab />;
  } else {
    const rootNode = activeTop.node;
    // If there are children, render second-level tabs, else show the rootNode's fields
    if (childNodes.length > 0) {
      secondLevelTabs = (
        <TabBar
          tabs={secondLevelOptions}
          activeIndex={activeChildIndex ?? 0}
          onSelect={(idx) => setSelectedTabPathForType(elementType, [activeTopIndex, idx])}
        />
      );
      const childNode = childNodes[activeChildIndex ?? 0];
      content = <FieldsList fields={childNode.fields} values={values} onChange={onChange} />;
    } else {
      // No children; show root fields
      content = <FieldsList fields={rootNode.fields} values={values} onChange={onChange} />;
    }
  }

  return (
    <div className="px-3 md:px-6">
      {/* Top Title remains managed by EditPanel header */}
      <div className="space-y-4">
        <div
          data-tutorial-id="edit-panel-tabs"
          className={`space-y-4 sticky top-0 z-20 ${surfaces.primary} -mx-3 px-3 md:-mx-6 md:px-6 pt-2 pb-1`}
        >
          {/* Basic + root groups */}
          {isMobileViewport ? (
            <div className="space-y-3">
              <select
                aria-label="Edit section"
                className={`w-full px-3 py-2 border rounded-md shadow-sm ${surfaces.primary} ${textColors.primary} ${typography.bodySmall} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${borders.default}`}
                value={String(activeTopIndex)}
                onChange={(e) => selectTop(Number(e.target.value))}
              >
                {topTabs.map((t, idx) => (
                  <option key={t.key} value={String(idx)}>
                    {t.label}
                  </option>
                ))}
              </select>

              {activeTop?.type === "group" && secondLevelOptions.length > 0 ? (
                <select
                  aria-label="Edit subsection"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm ${surfaces.primary} ${textColors.primary} ${typography.bodySmall} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${borders.default}`}
                  value={String(activeChildIndex ?? 0)}
                  onChange={(e) => {
                    const idx = Number(e.target.value);
                    setSelectedTabPathForType(elementType, [activeTopIndex, idx]);
                  }}
                >
                  {secondLevelOptions.map((t, idx) => (
                    <option key={t.key} value={String(idx)}>
                      {t.label}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
          ) : (
            <>
              <TabBar tabs={topTabs} activeIndex={activeTopIndex} onSelect={selectTop} />
              {/* Second level if applicable */}
              {secondLevelTabs}
            </>
          )}
        </div>
        {/* Content */}
        <div>
          {content}
        </div>
      </div>
    </div>
  );
};

export { EditTabs };


